import { useContractQuery } from './useContractQuery';

/* -------------------------------------------------------------- *
 *   AR v2 enums — kept in sync with AuditorRegistryV2.sol
 * -------------------------------------------------------------- */

export const AUDIT_CATEGORIES = [
  'Generic',
  'ScientificPeerReview',
  'ClinicalSafety',
  'Financial',
  'QualityAssurance',
  'RegulatoryOracle',
  'Programmatic',
] as const;
export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];

export const SENIORITY_LEVELS = ['Junior', 'Senior', 'Principal'] as const;
export type SeniorityLevel = (typeof SENIORITY_LEVELS)[number];

export const MILESTONE_TYPES = [
  'Generic',
  'Scientific',
  'Regulatory',
  'ClinicalData',
  'Manufacturing',
  'Commercial',
  'Operational',
] as const;
export type MilestoneType = (typeof MILESTONE_TYPES)[number];

export const FIRM_STATUSES = ['Inactive', 'Active', 'Frozen', 'Banned'] as const;
export type FirmStatus = (typeof FIRM_STATUSES)[number];

export const INCIDENT_SEVERITIES = ['Minor', 'Major', 'Critical'] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

/** Resolve enum index → human label safely. */
export function auditCategoryName(v: number | bigint | undefined): AuditCategory | null {
  if (v === undefined) return null;
  return (AUDIT_CATEGORIES[Number(v)] ?? null) as AuditCategory | null;
}
export function seniorityName(v: number | bigint | undefined): SeniorityLevel | null {
  if (v === undefined) return null;
  return (SENIORITY_LEVELS[Number(v)] ?? null) as SeniorityLevel | null;
}
export function milestoneTypeName(v: number | bigint | undefined): MilestoneType | null {
  if (v === undefined) return null;
  return (MILESTONE_TYPES[Number(v)] ?? null) as MilestoneType | null;
}
export function firmStatusName(v: number | bigint | undefined): FirmStatus | null {
  if (v === undefined) return null;
  return (FIRM_STATUSES[Number(v)] ?? null) as FirmStatus | null;
}

/** Sentinel returned by AuditorRegistryV2 view fns when a lookup fails. */
export const NOT_FOUND = (2n ** 256n) - 1n;

/* -------------------------------------------------------------- *
 *   Counts and aggregates
 * -------------------------------------------------------------- */

export function useFirmCount() {
  return useContractQuery<bigint>(['registry', 'firmCount'], { refetchInterval: 20_000 });
}

export function useFirmIdOf(address: string | null | undefined) {
  return useContractQuery<bigint>(
    ['registry', 'getFirmId', address ?? '0x0'],
    { enabled: !!address }
  );
}

/* -------------------------------------------------------------- *
 *   Firm data
 * -------------------------------------------------------------- */

/**
 * Tuple returned by AuditorRegistryV2.getFirm(firmId). Mirrors the function
 * signature exactly so caller can destructure or access by index.
 */
export type FirmRow = {
  wallet: string;
  name: string;
  jurisdiction: string;
  seniority: bigint;       // SeniorityLevel enum index
  status: bigint;          // FirmStatus enum index
  stake: bigint;
  reputation: bigint;
  dsmbVetoRights: boolean;
};

export function useFirm(firmId: number | bigint | null | undefined) {
  return useContractQuery<FirmRow>(
    ['registry', 'getFirm', BigInt(firmId ?? 0)],
    { enabled: firmId !== null && firmId !== undefined }
  );
}

/** Stats tuple — assignments closed/approved/rejected + strike counts. */
export type FirmStats = {
  assignmentsClosed: bigint;
  assignmentsApproved: bigint;
  assignmentsRejected: bigint;
  strikesMinor: bigint;
  strikesMajor: bigint;
  strikesCritical: bigint;
  lastAssignmentAt: bigint;
};

export function useFirmStats(firmId: number | bigint | null | undefined) {
  return useContractQuery<FirmStats>(
    ['registry', 'getFirmStats', BigInt(firmId ?? 0)],
    { enabled: firmId !== null && firmId !== undefined }
  );
}

export function useHasCategory(
  firmId: number | bigint | null | undefined,
  category: number /* AuditCategory enum index */
) {
  return useContractQuery<boolean>(
    ['registry', 'hasCategory', BigInt(firmId ?? 0), category],
    { enabled: firmId !== null && firmId !== undefined }
  );
}

/* -------------------------------------------------------------- *
 *   DSMB veto + eligibility checks
 * -------------------------------------------------------------- */

export function useHasDsmbVeto(address: string | null | undefined) {
  return useContractQuery<boolean>(
    ['registry', 'hasDsmbVeto', address ?? '0x0'],
    { enabled: !!address }
  );
}

export function useCanAuditMilestone(
  firmId: number | bigint | null | undefined,
  milestoneType: number /* MilestoneType enum index */
) {
  return useContractQuery<boolean>(
    ['registry', 'canAuditMilestone', BigInt(firmId ?? 0), milestoneType],
    { enabled: firmId !== null && firmId !== undefined }
  );
}

export function useCanSignMilestoneValue(
  firmId: number | bigint | null | undefined,
  weiAmount: bigint
) {
  return useContractQuery<boolean>(
    ['registry', 'canSignMilestoneValue', BigInt(firmId ?? 0), weiAmount],
    { enabled: firmId !== null && firmId !== undefined }
  );
}

/** Bundle check — used by AuditingPool gates. */
export function useIsEligible(
  address: string | null | undefined,
  milestoneType: number,
  weiAmount: bigint
) {
  return useContractQuery<boolean>(
    ['registry', 'isEligible', address ?? '0x0', milestoneType, weiAmount],
    { enabled: !!address }
  );
}

/* -------------------------------------------------------------- *
 *   Oracle attestations
 * -------------------------------------------------------------- */

export type Attestation = {
  issuer: string;
  issuedAt: bigint;
  expiresAt: bigint;
  evidenceHash: string;
  revoked: boolean;
  active: boolean;
};

export function useAttestation(
  firmId: number | bigint | null | undefined,
  certKey: string | null | undefined  // bytes32
) {
  return useContractQuery<Attestation>(
    ['registry', 'getAttestation', BigInt(firmId ?? 0), certKey ?? '0x' + '0'.repeat(64)],
    { enabled: firmId !== null && firmId !== undefined && !!certKey }
  );
}

export function useIsOracle(address: string | null | undefined) {
  return useContractQuery<boolean>(
    ['registry', 'isOracle', address ?? '0x0'],
    { enabled: !!address }
  );
}

/* -------------------------------------------------------------- *
 *   Tier constants (public state vars / constants on the contract)
 * -------------------------------------------------------------- */

export function useMinStakeJunior()    { return useContractQuery<bigint>(['registry', 'MIN_STAKE_JUNIOR']); }
export function useMinStakeSenior()    { return useContractQuery<bigint>(['registry', 'MIN_STAKE_SENIOR']); }
export function useMinStakePrincipal() { return useContractQuery<bigint>(['registry', 'MIN_STAKE_PRINCIPAL']); }
export function useJuniorMilestoneCap() { return useContractQuery<bigint>(['registry', 'JUNIOR_MILESTONE_CAP']); }
export function useSeniorMilestoneCap() { return useContractQuery<bigint>(['registry', 'SENIOR_MILESTONE_CAP']); }
export function useUnstakeCooldown()   { return useContractQuery<bigint>(['registry', 'UNSTAKE_COOLDOWN']); }
export function useMinStakeOverride()  { return useContractQuery<bigint>(['registry', 'minStakeOverride']); }
